import './styles.scss';

import { PlusOutlined } from '@ant-design/icons';
import { CustomSearch } from '@components/modules/custom-search';
import { FilterSelection } from '@components/modules/filter-selection';
import { FilterStatus } from '@components/modules/filter-status';
import { CustomPagination } from '@components/modules/pagination';
import { PAGE_SIZE, ROOT_TENANT } from '@constants/index.constant';
import { PERMISSIONS } from '@constants/permission';
import { AppSubscriptionStatusEnum } from '@enums/app-subscription-status.enum';
import { IMarketplace } from '@interfaces/marketplace';
import {
    CreateButton,
    DeleteButton,
    getDefaultSortOrder,
    mapAntdSorterToCrudSorting,
    ShowButton,
    useForm,
    useModal,
} from '@refinedev/antd';
import {
    LogicalFilter,
    useApiUrl,
    useCustom,
    useDelete,
    useList,
    usePermissions,
    useTable,
    useTranslate,
    useUpdate,
} from '@refinedev/core';
import { formatDate, isHavingPermission } from '@utils/resource';
import {
    Button,
    Dropdown,
    FormInstance,
    Menu,
    notification,
    Popconfirm,
    Table,
    TablePaginationConfig,
} from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { useEffect, useMemo, useState } from 'react';

import Response from '../../data-access/responses/response';
import { DataProviderNameEnum } from '../../dataProvider';
import { UserStatusEnum } from '../../enums/user-status.enum';
import { UserTypeEnum } from '../../enums/user-type.enum';
import { IUser } from '../../interfaces/user';
import ResellerModal from './reseller-modal';
const LIST_FILTER_STATUS = [
    {
        label: 'Active',
        value: AppSubscriptionStatusEnum.Active,
    },
    {
        label: 'In Complete',
        value: AppSubscriptionStatusEnum.InComplete,
    },
    {
        label: 'In Complete Expired',
        value: AppSubscriptionStatusEnum.InCompleteExpired,
    },
    {
        label: 'Trailing',
        value: AppSubscriptionStatusEnum.Trailing,
    },
    {
        label: 'Cancelled',
        value: AppSubscriptionStatusEnum.Cancelled,
    },
    {
        label: 'Open',
        value: AppSubscriptionStatusEnum.Open,
    },
];

const APPLICATION_RESOURCE_URL = 'v1/application';

export const SaSubscriptionIndex: React.FC = () => {
    const translate = useTranslate();
    const [optionsApplications, setOptionsApplications] = useState<
        { label: string; value: string }[]
    >([]);
    const [resellerSelected, setResellerSelected] = useState<string>();
    const [currentReseller, setCurrentReseller] = useState<IUser.IUserDto>();
    const [appSubSelected, setAppSubSelected] = useState<string>();
    const dataProviderName = DataProviderNameEnum.INTERNAL_CRM;
    const internalApiUrl = useApiUrl(dataProviderName);
    const { data: permissions } = usePermissions<string[]>();

    type AppSubscriptionTableType = IMarketplace.IAppSubscription & {
        // resellerId?: string;
        // resellerName?: string;
        // resellerStatus?: UserStatusEnum;
        reseller?: IUser.IUserDto;
        isCreatedBySA?: boolean;
        resellerDeleted?: boolean;
        showChangeReseller?: boolean;
    };

    const {
        setFilters,
        filters,
        tableQueryResult,
        current,
        setCurrent,
        pageSize,
        setPageSize,
        sorters,
        setSorters,
    } = useTable<AppSubscriptionTableType>({
        resource: 'v1/application-subscriptions',
        sorters: {
            initial: [
                {
                    field: 'currentPeriodEnd',
                    order: 'asc',
                },
            ],
        },
        pagination: {
            pageSize: PAGE_SIZE,
        },
        syncWithLocation: true,
    });
    const tableData = tableQueryResult?.data?.data || [];

    const { data: openInvoiceIdsResponse } = useCustom<Response<string[]>>({
        url: `${internalApiUrl}/v1/application-subscriptions/has-open-invoice`,
        method: 'post',
        config: {
            payload: {
                subscriptionIds: tableData?.map((item) => item.id),
            },
        },
        queryOptions: { enabled: !!tableData.length },
    });

    const openInvoiceIds = openInvoiceIdsResponse?.data?.data || [];

    const hasOpenInvoicesMap = useMemo(() => {
        return openInvoiceIds?.reduce<Record<string, boolean>>((acc, id) => {
            acc[id] = true;
            return acc;
        }, {});
    }, [openInvoiceIds]);

    const { data: createdByUsersRes, refetch: getCreatedByUsers } = useCustom<IUser.IUserDto[]>({
        method: 'post',
        url: `${internalApiUrl}/v1/users/by-ids`,
        config: {
            payload: {
                ids: tableData.map((item) => item.createdBy),
                withDeleted: true,
            },
        },
        queryOptions: { enabled: false },
    });

    useEffect(() => {
        if (tableData.length) {
            getCreatedByUsers();
        }
    }, [tableData]);
    const createdByUsers = createdByUsersRes?.data || [];
    const resellerMap = useMemo(() => {
        return createdByUsers.reduce<{
            reseller: Record<string, IUser.IUserDto>;
            sa: Record<string, IUser.IUserDto>;
        }>(
            (acc, user) => {
                if (user.userType === UserTypeEnum.RESELLER && user.id) {
                    acc.reseller[user.id] = user;
                }
                if (
                    user.userType === UserTypeEnum.TENANT &&
                    user.tenantId === ROOT_TENANT &&
                    user.id
                ) {
                    acc.sa[user.id] = user;
                }
                return acc;
            },
            { reseller: {}, sa: {} },
        );
    }, [createdByUsers]);
    tableData.forEach((item) => {
        if (item.createdBy && resellerMap.reseller[item.createdBy]) {
            const reseller = resellerMap.reseller[item.createdBy];
            item.reseller = reseller;
            if (reseller.deletedAt) {
                item.resellerDeleted = true;
            }
        }
        if (item.createdBy && resellerMap.sa[item.createdBy]) {
            item.isCreatedBySA = true;
        }
    });

    const { data: listApplications } = useList<IMarketplace.IApplication>({
        resource: `${APPLICATION_RESOURCE_URL}`,
        config: {
            hasPagination: false,
            filters: [
                {
                    field: 'includeUnpublish',
                    operator: 'eq',
                    value: true,
                },
            ],
        },
        queryOptions: {
            enabled: isHavingPermission(permissions || [], PERMISSIONS.MARKETPLACE_LIST),
        },
    });

    const { mutate: deleteAction, isLoading: isDeleting } = useDelete();

    useEffect(() => {
        if (listApplications?.data.length) {
            const options = listApplications.data.map((app) => {
                return {
                    label: app.title,
                    value: app.id,
                };
            });
            setOptionsApplications(options);
        }
    }, [listApplications]);

    const cancelSubscription = (item: IMarketplace.IAppSubscription) => {
        if (!item?.id) return;

        deleteAction(
            {
                resource: `v1/application-subscriptions/manual-cancel`,
                id: item.id,
                successNotification: false,
                errorNotification: false,
            },
            {
                onSuccess: () => {
                    notification.success({
                        message: translate('subscriptions.messages.cancel_subscription_success'),
                    });

                    tableQueryResult.refetch();
                    return;
                },

                onError: () => {
                    notification.error({
                        message: translate('subscriptions.messages.cancel_subscription_failed'),
                    });
                },
            },
        );
    };

    const handleResellerModalCancel = () => {
        closeResellerModal();
        setResellerSelected(undefined);
        setAppSubSelected(undefined);
        setCurrentReseller(undefined);
        partnerForm?.resetFields();
    };

    const { mutate: updateReseller, isLoading: isResellerUpdating } = useUpdate();

    const handleAddReseller = (appSubInvoiceId: string, resellerId: string | null) => {
        updateReseller(
            {
                dataProviderName,
                resource: `v1/application-subscriptions/update-reseller`,
                values: {
                    resellerId,
                },
                id: appSubInvoiceId,
                successNotification: false,
                errorNotification: false,
            },
            {
                onError: (error) => {
                    console.error(error);
                    const errorMessage = error.response?.data?.error;
                    if (errorMessage) {
                        notification.error({
                            message: translate(`notifications.errors.${errorMessage}`, {
                                ns: 'common',
                            }),
                        });
                    }
                },
                onSuccess: (res) => {
                    notification.success({
                        message: translate('subscriptions.messages.update_reseller_success'),
                    });
                    tableQueryResult.refetch();
                },
            },
        );
    };

    const handleDeleteReseller = (invoiceId: string) => {
        handleAddReseller(invoiceId, null);
    };

    const {
        modalProps: resellerModalProps,
        show: showResellerModal,
        close: closeResellerModal,
    } = useModal({
        modalProps: {
            onCancel: handleResellerModalCancel,
        },
    });

    const { form: partnerForm } = useForm();

    const menu = (id: string, record: AppSubscriptionTableType) => {
        const hasOpenInvoice = hasOpenInvoicesMap[record.id];
        return (
            <Menu mode="vertical">
                <Menu.Item
                    key="1"
                    icon={
                        <ShowButton
                            resource="report_subscription"
                            recordItemId={record.id}
                            children={translate('subscriptions.action.viewBillingHistory')}
                            className="icon-action-subscription-list"
                        />
                    }
                ></Menu.Item>
                {/* {hasOpenInvoice ? (
                    <Menu.Item
                        style={{ padding: 0 }}
                        key="2"
                        icon={
                            <Button
                                onClick={() => {
                                    showResellerModal();
                                    setAppSubSelected(record.id);
                                    partnerForm.setFieldValue('partner', record.reseller?.id);
                                    setCurrentReseller(record.reseller);
                                }}
                                className="icon-action-subscription-list"
                                icon={
                                    <img
                                        src="/images/icons/edit.svg"
                                        alt="edit"
                                        className="cursor-pointer mr-2 w-[14px] h-[14px]"
                                    />
                                }
                                style={{
                                    padding: '5px 24px 5px 12px',
                                    margin: 0,
                                }}
                            >
                                <span>
                                    {translate('subscriptions.action.change_reseller', {
                                        ns: 'common',
                                    })}
                                </span>
                            </Button>
                        }
                    ></Menu.Item>
                ) : null} */}
                {AppSubscriptionStatusEnum.Cancelled !== record.status ? (
                    !record.cancelAtPeriodEnd || record.isManual ? (
                        <Menu.Item key="3">
                            <Popconfirm
                                key="delete"
                                okText={translate('subscriptions.action.confirm')}
                                cancelText={translate('subscriptions.action.cancel')}
                                okType="danger"
                                title={translate(
                                    'subscriptions.messages.cancel_subscription_confirm',
                                )}
                                onConfirm={(e): void => {
                                    e?.preventDefault();
                                    cancelSubscription(record);
                                }}
                            >
                                <DeleteButton
                                    loading={isDeleting}
                                    resource="report_subscription"
                                    recordItemId={record.id}
                                    children={translate('subscriptions.action.cancel_subscription')}
                                    className="icon-action-subscription-list"
                                />
                            </Popconfirm>
                        </Menu.Item>
                    ) : null
                ) : null}
            </Menu>
        );
    };

    const onChangeFilter = (event: any) => {
        setFilters((prev) => {
            const newFilter = prev;
            const index = newFilter.findIndex((item) => (item as LogicalFilter).field === 'filter');
            if (index !== -1) {
                (newFilter[index] as LogicalFilter).value = event.target.value;
            } else {
                newFilter.push({
                    field: 'filter',
                    operator: 'eq',
                    value: event.target.value,
                });
            }

            return newFilter;
        });

        setCurrent(1);
    };

    const onChangeStatus = (status: string) => {
        setFilters((prev) => {
            const newFilter = prev;
            const index = newFilter.findIndex((item) => (item as LogicalFilter).field === 'status');
            if (index !== -1) {
                (newFilter[index] as LogicalFilter).value = status;
            } else {
                newFilter.push({
                    field: 'status',
                    operator: 'eq',
                    value: status,
                });
            }
            return newFilter;
        });

        setCurrent(1);
    };

    const onChangeApplication = (value: string) => {
        setFilters((prev) => {
            const newFilter = prev;
            const index = newFilter.findIndex(
                (item) => (item as LogicalFilter).field === 'applicationId',
            );
            if (index !== -1) {
                (newFilter[index] as LogicalFilter).value = value;
            } else {
                newFilter.push({
                    field: 'applicationId',
                    operator: 'eq',
                    value: value,
                });
            }
            return newFilter;
        });

        setCurrent(1);
    };

    const onChangeTable = (
        pagination: TablePaginationConfig,
        tableFilters: Record<
            string,
            (string | number | boolean) | (string | number | boolean)[] | null
        >,
        sorter: SorterResult<any> | SorterResult<any>[],
    ) => {
        if (sorter && Object.keys(sorter).length > 0) {
            // Map Antd:Sorter -> refine:CrudSorting
            const crudSorting = mapAntdSorterToCrudSorting(sorter);
            setSorters(crudSorting);
        }
    };

    return (
        <>
            <section className="item-list-container sa-subscription-page-wrapper ">
                <div className="list-header">
                    <div className="filter-wrapper">
                        <CustomSearch
                            placeholder="Search Tenant"
                            className={'search-item custom-width'}
                            onChange={(event: any) => onChangeFilter(event)}
                            allowClear={true}
                            defaultValue={filters?.find((f: any) => f.field === 'filter')?.value}
                        />
                        <FilterStatus
                            placeholder="Select status"
                            listStatus={LIST_FILTER_STATUS}
                            className="search-status mr-8"
                            defaultValue={filters?.find((f: any) => f.field === 'status')?.value}
                            onChangeStatus={(status: string) => onChangeStatus(status)}
                        />
                        {isHavingPermission(permissions || [], PERMISSIONS.MARKETPLACE_LIST) && (
                            <FilterSelection
                                placeholder="Select Application"
                                listValue={optionsApplications}
                                className="search-applications"
                                defaultValue={
                                    filters?.find((f: any) => f.field === 'applicationId')?.value
                                }
                                onChangeValue={(appId: string) => onChangeApplication(appId)}
                            />
                        )}
                    </div>
                    {isHavingPermission(
                        permissions || [],
                        PERMISSIONS.REPORT_SUBSCRIPTION_CREATE,
                    ) ? (
                        <CreateButton icon={false} className="btn-add-new" type="primary">
                            <PlusOutlined />
                            {translate('add_new', { ns: 'common' })}
                        </CreateButton>
                    ) : null}
                </div>
                <div className="overflow-hidden">
                    <div className="list-content table-wrapper">
                        <Table
                            pagination={false}
                            dataSource={tableQueryResult?.data?.data}
                            rowKey="id"
                            loading={tableQueryResult.isLoading}
                            onChange={onChangeTable}
                        >
                            <Table.Column
                                sorter
                                title={<>{translate('subscriptions.expiredDate')}</>}
                                dataIndex="currentPeriodEnd"
                                key="currentPeriodEnd"
                                defaultSortOrder={getDefaultSortOrder('currentPeriodEnd', sorters)}
                                render={(item, _) => {
                                    return <p className="table-tbody-text">{formatDate(item)}</p>;
                                }}
                            />
                            <Table.Column
                                title={<>{translate('subscriptions.applicationName')}</>}
                                dataIndex="application"
                                key="application"
                                render={(application, _) => {
                                    return <p className="table-tbody-text">{application?.title}</p>;
                                }}
                            />
                            <Table.Column
                                title={<>{translate('subscriptions.tenantName')}</>}
                                dataIndex="tenant"
                                key="tenant"
                                render={(tenant, _) => {
                                    return <p className="table-tbody-text">{tenant?.name}</p>;
                                }}
                            />
                            <Table.Column
                                title={<>{translate('subscriptions.statusTitle')}</>}
                                dataIndex="status"
                                key="status"
                                render={(status, _) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {translate(
                                                'subscriptions.subscriptionStatus.' +
                                                    status.toString(),
                                            )}
                                        </p>
                                    );
                                }}
                            />

                            <Table.Column
                                title={<>{translate('subscriptions.payment_method.title')}</>}
                                dataIndex="isManual"
                                key="tenant"
                                render={(isManual, _, __) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {isManual
                                                ? translate('subscriptions.payment_method.manual')
                                                : translate('subscriptions.payment_method.credit')}
                                        </p>
                                    );
                                }}
                            />

                            <Table.Column
                                title={
                                    <>{translate('subscriptions.subscription_reseller.title')}</>
                                }
                                dataIndex="resellerName"
                                key="resellerName"
                                render={(resellerName, record: AppSubscriptionTableType) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {!record.resellerDeleted &&
                                                record.reseller &&
                                                `${record.reseller?.firstName} ${record.reseller?.lastName}`}
                                        </p>
                                    );
                                }}
                            />

                            <Table.Column
                                dataIndex="id"
                                key="action"
                                render={(id, record: IMarketplace.IAppSubscription) => (
                                    <div className="flex justify-between items-center">
                                        <Dropdown
                                            overlay={menu(id, record)}
                                            placement="bottomRight"
                                            className="w-8 ml-auto"
                                            arrow
                                            overlayClassName="contact-dropdown-container"
                                            trigger={['click']}
                                        >
                                            <div className="">
                                                <img
                                                    src="/images/icons/dots-vertical.svg"
                                                    alt="more"
                                                    className="cursor-pointer"
                                                />
                                            </div>
                                        </Dropdown>
                                    </div>
                                )}
                            />
                        </Table>
                    </div>
                </div>
                <div className="pagination-container pt-3 pb-4 px-6">
                    <CustomPagination
                        pageSize={pageSize}
                        total={tableQueryResult?.data?.total}
                        current={current}
                        onChange={(value: number, size: number) => {
                            setCurrent(value);
                            setPageSize(size);
                        }}
                    />
                </div>
            </section>
            <ResellerModal
                closeResellerModal={closeResellerModal}
                handleAddReseller={handleAddReseller}
                handleDeleteReseller={handleDeleteReseller}
                handleResellerModalCancel={handleResellerModalCancel}
                invoiceSelected={appSubSelected}
                isResellerUpdating={isResellerUpdating}
                resellerModalProps={resellerModalProps}
                resellerSelected={resellerSelected}
                setResellerSelected={setResellerSelected}
                currentReseller={currentReseller}
                partnerForm={partnerForm}
            />
        </>
    );
};
